/* The sync engine.
 *
 * A foreground loop, because iOS has no Background Sync and pretending
 * otherwise loses data. It runs on app open, on visibilitychange→visible, on
 * the `online` event, on a 30s connectivity probe, and after every local
 * commit.
 *
 * Two lanes, in order. Records go first — they are small, and an evidence row
 * is useful to the team the moment it lands even if its photos are still
 * climbing. Then media, oldest first, AUDIO BEFORE PHOTOS: the spoken note is
 * the analysis-critical payload (Brandon, q.06), and a 40KB voice memo should
 * never sit behind a 6MB photo on a bar of signal.
 *
 * There are no conflicts to handle. Everything is append-only, so two devices
 * correcting the same field produce two rows and the projection picks one.
 * That is the entire reason for the architecture, and it is why this file is
 * as short as it is.
 */

import { createApi, PILL, SYNC_NARRATION, type Media, type PushEntry } from '@kilroy/shared';
import { db, getMeta, setMeta } from '../db/db';
import { onCommit } from '../db/commit';
import { readMedia } from '../media/opfs';

const API_BASE =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://localhost:8787' : '');
/* The token is read from IndexedDB on every request rather than captured once.
 *
 * The engine outlives any single session — it starts at boot and runs for the
 * life of the tab — so holding a token would mean holding whatever was true at
 * launch. Reading it per call costs nothing and is always current. */
let cachedToken: string | null = null;
export function setAccessToken(token: string | null) {
  cachedToken = token;
}

export const api = createApi({
  base: API_BASE,
  apiKey: import.meta.env.VITE_KILROY_API_KEY,
  accessToken: () => cachedToken,
});

/** iOS `online` lies, so we ask our own server every 30 seconds. */
const PROBE_INTERVAL_MS = 30_000;
/** Records are pushed in one request per drain; this caps a cold-start batch. */
const PUSH_BATCH = 100;

export type Phase = 'offline' | 'syncing' | 'media' | 'synced';

export interface SyncStatus {
  phase: Phase;
  /** Records waiting. */
  queued: number;
  /** Media files waiting. */
  mediaQueued: number;
  /** Rolling narration line for the status sheet. */
  narration: string;
  pill: string;
  lastSyncedAt: string | null;
  lastError: string | null;
}

/* ── Status store. useSyncExternalStore reads this. ─────────────────────── */

let status: SyncStatus = {
  phase: 'offline',
  queued: 0,
  mediaQueued: 0,
  narration: '',
  pill: PILL.offline(0),
  lastSyncedAt: null,
  lastError: null,
};

const subs = new Set<() => void>();

function set(patch: Partial<SyncStatus>) {
  const next = { ...status, ...patch };
  next.pill =
    next.phase === 'offline'
      ? PILL.offline(next.queued + next.mediaQueued)
      : next.phase === 'syncing'
        ? PILL.syncing(next.queued)
        : next.phase === 'media'
          ? PILL.media(next.mediaQueued)
          : PILL.synced;
  status = next;
  for (const fn of subs) fn();
}

export const subscribe = (fn: () => void) => {
  subs.add(fn);
  return () => subs.delete(fn);
};
export const getStatus = () => status;

/* ── Backoff. Deliberately gentle: a phone in a truck regains signal in bursts
      and we want to catch the burst, not sleep through it. ───────────────── */

const backoffMs = (attempts: number) =>
  Math.min(2 ** Math.min(attempts, 6) * 1000, 60_000);

/* ── The engine ─────────────────────────────────────────────────────────── */

let running = false;
let timer: number | null = null;
let disposers: (() => void)[] = [];

async function counts() {
  const all = await db.outbox.toArray();
  return {
    queued: all.filter((o) => o.kind !== 'media').length,
    mediaQueued: all.filter((o) => o.kind === 'media').length,
  };
}

/** Push every queued record in one idempotent request. */
async function drainRecords(deviceId: string): Promise<boolean> {
  const rows = (await db.outbox.orderBy('seq').toArray())
    .filter((o) => o.kind !== 'media' && o.next_attempt_at <= Date.now())
    .slice(0, PUSH_BATCH);
  if (rows.length === 0) return true;

  set({ phase: 'syncing', narration: SYNC_NARRATION.draining });

  const entries: PushEntry[] = [];
  for (const row of rows) {
    const record = await loadRecord(row.kind, row.ref_id);
    // The row vanished under us (only possible via a dev reset). Drop the
    // outbox entry rather than retry a ghost forever.
    if (!record) {
      await db.outbox.delete(row.seq!);
      continue;
    }
    entries.push({ client_seq: row.seq!, kind: row.kind as PushEntry['kind'], record });
  }
  if (entries.length === 0) return true;

  const res = await api.push({
    device_id: deviceId,
    device_sent_at: new Date().toISOString(),
    entries,
  });

  if (!res) {
    // No answer. Back off every attempted row; nothing is lost.
    for (const row of rows) {
      await db.outbox.update(row.seq!, {
        attempts: row.attempts + 1,
        next_attempt_at: Date.now() + backoffMs(row.attempts + 1),
      });
    }
    return false;
  }

  // Clock skew is recorded, not corrected. captured_at stays device truth.
  await setMeta('last_skew_ms', Date.parse(res.server_time) - Date.now());

  // Outbox rows are deleted ONLY on ack.
  await db.transaction('rw', db.outbox, db.evidence, async () => {
    for (const seq of res.acked_seqs) {
      const row = rows.find((r) => r.seq === seq);
      await db.outbox.delete(seq);
      if (row?.kind === 'evidence') {
        await db.evidence.update(row.ref_id, { sync_state: 'synced' });
      }
    }
  });

  for (const r of res.rejected) {
    // Rejections are schema-level only. Keep the row, surface it, move on —
    // a malformed record is a bug to fix, not data to discard.
    await db.outbox.update(r.seq, {
      attempts: 99,
      next_attempt_at: Date.now() + 3_600_000,
      last_error: r.reason,
    });
    set({ lastError: `Record ${r.seq}: ${r.reason}` });
  }

  return res.rejected.length === 0;
}

async function loadRecord(kind: string, id: string): Promise<unknown> {
  switch (kind) {
    case 'evidence':
      return db.evidence.get(id);
    case 'correction':
      return db.corrections.get(id);
    case 'status':
      return db.statuses.get(id);
    case 'ranking':
      return db.priorityRankings.get(id);
    case 'rectified':
      return db.priorityRectified.get(id);
    default:
      return null;
  }
}

/** Find the media descriptor and its parent evidence id. */
async function findMedia(mediaId: string): Promise<{ media: Media; evidenceId: string } | null> {
  const all = await db.evidence.toArray();
  for (const e of all) {
    const media = e.media.find((m) => m.id === mediaId);
    if (media) return { media, evidenceId: e.id };
  }
  return null;
}

async function drainMedia(): Promise<void> {
  const rows = (await db.outbox.orderBy('seq').toArray()).filter(
    (o) => o.kind === 'media' && o.next_attempt_at <= Date.now(),
  );
  if (rows.length === 0) return;

  // Audio first, then photos, oldest within each. The spoken note is what the
  // analysis actually needs.
  const withKind = await Promise.all(
    rows.map(async (row) => ({ row, found: await findMedia(row.ref_id) })),
  );
  withKind.sort((a, b) => {
    const rank = (k?: string) => (k === 'audio' ? 0 : 1);
    const d = rank(a.found?.media.kind) - rank(b.found?.media.kind);
    return d !== 0 ? d : a.row.seq! - b.row.seq!;
  });

  set({ phase: 'media' });

  for (const { row, found } of withKind) {
    if (!found) {
      await db.outbox.delete(row.seq!);
      continue;
    }
    const file = await readMedia(found.media.file);
    if (!file) {
      // The bytes are gone. This should be impossible — flag it loudly rather
      // than silently ack an upload that never happened.
      await db.outbox.update(row.seq!, {
        attempts: 99,
        next_attempt_at: Date.now() + 3_600_000,
        last_error: 'local file missing',
      });
      set({ lastError: `Media ${found.media.file} missing on device` });
      continue;
    }

    const intent = await api.mediaIntent(found.media.id, file.size, found.media.mime);
    const ok =
      !!intent &&
      (await api.uploadMedia(intent.url, intent.method, file)) &&
      (await api.confirmMedia(found.media.id));

    if (ok) {
      await db.transaction('rw', db.outbox, db.evidence, async () => {
        await db.outbox.delete(row.seq!);
        const parent = await db.evidence.get(found.evidenceId);
        if (parent) {
          await db.evidence.update(found.evidenceId, {
            media: parent.media.map((m) =>
              m.id === found.media.id ? { ...m, confirmed: true } : m,
            ),
          });
        }
      });
    } else {
      await db.outbox.update(row.seq!, {
        attempts: row.attempts + 1,
        next_attempt_at: Date.now() + backoffMs(row.attempts + 1),
      });
      return; // one failure means no signal; stop hammering
    }
  }
}

async function pullChanges(deviceId: string): Promise<void> {
  const cursor = await getMeta<number>('pull_cursor', 0);
  const res = await api.pull(cursor, deviceId);
  if (!res) return;

  await db.transaction(
    'rw',
    [db.evidence, db.assignments, db.priorityItems, db.priorityRankings, db.priorityRectified, db.corrections, db.statuses, db.users],
    async () => {
      // Never clobber a local record that hasn't been pushed yet.
      for (const e of res.evidence) {
        const local = await db.evidence.get(e.id);
        if (local && local.sync_state === 'local') continue;
        await db.evidence.put({ ...e, sync_state: 'synced' });
      }
      await db.assignments.bulkPut(res.assignments);
      await db.priorityItems.bulkPut(res.priority_items);
      await db.priorityRankings.bulkPut(
        res.priority_rankings.map((r) => ({ ...r, sync_state: 'synced' as const })),
      );
      await db.priorityRectified.bulkPut(
        res.priority_rectified.map((r) => ({ ...r, sync_state: 'synced' as const })),
      );
      await db.corrections.bulkPut(
        res.corrections.map((c) => ({ ...c, sync_state: 'synced' as const })),
      );
      await db.statuses.bulkPut(
        res.statuses.map((s) => ({ ...s, sync_state: 'synced' as const })),
      );
      // The roster is keyed by slug and the server returns real uuids. Without
      // this the phone renders a uuid where a name belongs — which on a screen
      // whose job is saying who asked for what is worse than useless.
      if (res.users.length) await db.users.bulkPut(res.users);
    },
  );

  await setMeta('pull_cursor', res.cursor);
}

let inFlight: Promise<void> | null = null;

export function kick(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = runOnce().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runOnce(): Promise<void> {
  const { queued, mediaQueued } = await counts();

  const reachable = await api.probe();
  if (!reachable) {
    set({ phase: 'offline', queued, mediaQueued });
    return;
  }

  const deviceId = await getMeta<string>('device_id', '');
  if (!deviceId) return;

  await drainRecords(deviceId);
  await pullChanges(deviceId);
  await drainMedia();

  const after = await counts();
  const drained = queued + mediaQueued > 0;

  set({
    ...after,
    phase: after.queued > 0 ? 'syncing' : after.mediaQueued > 0 ? 'media' : 'synced',
    // The narration only speaks when there was something to narrate. "All
    // synced" on a day with nothing captured is a sentence about nothing, and
    // saying it every launch would wear the line out before the demo.
    narration: drained
      ? after.queued + after.mediaQueued === 0
        ? SYNC_NARRATION.synced
        : SYNC_NARRATION.draining
      : status.narration,
    lastSyncedAt: new Date().toISOString(),
  });
}

export function startEngine(): void {
  if (running) return;
  running = true;

  const trigger = () => void kick();

  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trigger();
  });
  disposers.push(onCommit(trigger));
  disposers.push(() => window.removeEventListener('online', trigger));

  timer = window.setInterval(trigger, PROBE_INTERVAL_MS);
  trigger();
}

export function stopEngine(): void {
  running = false;
  if (timer) clearInterval(timer);
  timer = null;
  disposers.forEach((d) => d());
  disposers = [];
}

/** Manual "retry now" from the sync sheet. Resets backoff so the tap does
 *  something visible — waiting out a 60s timer feels like a broken button. */
export async function retryNow(): Promise<void> {
  await db.outbox.toCollection().modify({ next_attempt_at: 0 });
  await kick();
}

/** Refresh counts without touching the network — used after a local commit so
 *  the pill moves the instant you hit Save. */
export async function refreshCounts(): Promise<void> {
  const c = await counts();
  set({
    ...c,
    phase: status.phase === 'synced' && c.queued + c.mediaQueued > 0 ? 'offline' : status.phase,
  });
}
